/* eslint-disable no-restricted-imports */
/* eslint-disable functional/no-loop-statement */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { ProductDetailsCarousel, ProductDetailsCarouselHandle } from '@/components/blocks/ProductDetails/Carousel';
import { productDetailsImageSX } from '@/components/blocks/ProductDetails/styles/image';
import { ProductDetailsGalleryModal } from '@/components/blocks/ProductDetailsGalleryModal';
import { ProductImage } from '@/components/blocks/ProductImage';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { ProductType, Ribbon } from '@/data/types/Product';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { Grid, Skeleton } from '@mui/material';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

const IMAGE_ZOOM_PARTIAL_URL = `wcsstore/ExtendedSitesCatalogAssetStore/images/catalog/zoom`;
const IMAGE_ZOOM_URL = `https://dpjye2wk9gi5z.cloudfront.net/${IMAGE_ZOOM_PARTIAL_URL}`;
const IMAGE_THUMBNAIL_PARTIAL_URL = `wcsstore/ExtendedSitesCatalogAssetStore/images/catalog/thumbnail`;
const IMAGE_THUMBNAIL_URL = `https://dpjye2wk9gi5z.cloudfront.net/${IMAGE_THUMBNAIL_PARTIAL_URL}`;
const IMAGE_DEFAULT_PARTIAL_URL = `images/catalog/default`;
export const IMAGE_DEFAULT_URL = `https://dpjye2wk9gi5z.cloudfront.net/wcsstore/ExtendedSitesCatalogAssetStore/${IMAGE_DEFAULT_PARTIAL_URL}`;

export const getBundleImageUrl = (basePath: string, partNumber: string, index: number) =>
	`${basePath}/${partNumber}V${index}.jpg`;

export const generateBundleDefaultImageUrl = (partNumber: string, index: number) =>
	`${IMAGE_DEFAULT_URL}/${partNumber.slice(-12)}V${index}.jpg`;

const existsImage = (imageUrl: string) =>
	new Promise<string | null>((resolve) => {
		const image = new Image();
		image.src = imageUrl;
		image.onload = () => resolve(imageUrl);
		image.onerror = () => resolve(null);
	});

export const searchImage = async (partNumber: string, index: number) => {
	try {
		const path = `${partNumber}V${index}.jpg`;

		const imageUrlThumbnail = getBundleImageUrl(IMAGE_THUMBNAIL_URL, partNumber, index);
		const existsThumbnail = await existsImage(imageUrlThumbnail);

		if (existsThumbnail) {
			return { type: 'thumbnail', path } as const;
		}

		const imageUrlDefault = getBundleImageUrl(IMAGE_DEFAULT_URL, partNumber, index);
		const existsDefault = await existsImage(imageUrlDefault);

		if (existsDefault) {
			return { type: 'default', path } as const;
		}
	} catch {
		return null;
	}
};

const searchImages = async (partNumber: string) => {
	const images: { type: 'thumbnail' | 'default'; path: string }[] = [];

	for (let i = 1; i <= 7; i++) {
		const image = await searchImage(partNumber, i);
		if (image) {
			images.push(image);
		}
	}

	return images;
};

const useSearchImages = (partNumber?: string | null, name?: string | null) => {
	const [images, setImages] = useState<{ type: 'thumbnail' | 'default'; path: string }[]>([]);

	useEffect(() => {
		if (partNumber) {
			searchImages(partNumber).then(setImages);
		}
	}, [partNumber]);

	const computedImages = useMemo(
		() =>
			images.map((image) => ({
				name: name ?? image.path,
				thumbnail: `${image.type === 'thumbnail' ? IMAGE_THUMBNAIL_URL : IMAGE_DEFAULT_URL}/${
					image.path
				}`,
				thumbnailRaw: `${
					image.type === 'thumbnail' ? IMAGE_THUMBNAIL_PARTIAL_URL : IMAGE_DEFAULT_PARTIAL_URL
				}/${image.path}`,
				fullImage: `${IMAGE_ZOOM_URL}/${image.path}`,
				fullImageRaw: `${IMAGE_DEFAULT_PARTIAL_URL}/${image.path}`,
				sequence: '-1000.0',
			})),
		[images, name]
	);

	return computedImages;
};

export const extractCodeFromPath = (path: string): string | null => {
	const match = path.match(/\/([\d\-]+)V\d+\.\w+$/);
	return match ? match[1] : null;
};

interface BundleGalleryProps {
	product?: ProductType;
}

// eslint-disable-next-line complexity
export const BundleGallery: FC<BundleGalleryProps> = ({ product }) => {
	const { name } = getProductDisplayInfo(product, product);
	const [index, setIndex] = useState<number>(0);
	const openImageDrawer = useIsOpen(false);
	const carouselRef = useRef<ProductDetailsCarouselHandle>(null);
	const partNumber = useMemo(() => {
		let value = product?.thumbnail ? extractCodeFromPath(product?.thumbnail) : undefined;
		if (!value) {
			value = product?.partNumber?.slice(-12);
		}

		return value;
	}, [product]);

	const slidesRaw = useSearchImages(partNumber, name);
	const slides = useMemo(() => {
		if (slidesRaw.length === 0) {
			return [];
		}

		const videos = product?.attachments
			?.filter((item) => {
				if (item.mimeType === 'content/url') {
					return (
						item.attachmentAssetPath.includes('https://') &&
						item.attachmentAssetPath.includes('.mp4')
					);
				}
				return false;
			})
			?.map((item) => ({
				name,
				thumbnail: item.attachmentAssetPath,
				thumbnailRaw: item.attachmentAssetPath,
				fullImage: item.attachmentAssetPath,
				fullImageRaw: item.attachmentAssetPath,
				sequence: '-1000.0',
			}));
		const [firstSlide, ...restSlides] = slidesRaw;

		return [firstSlide, ...(videos ?? []), ...restSlides];
	}, [slidesRaw, product, name]);

	const showCarousel = useMemo(() => (slides?.length ?? 0) > 0, [slides]);

	const slidesImage = useMemo(
		() => ((slides?.length ?? 0) > 1 ? slides[0]?.fullImage : slides[0]?.fullImage),
		[slides]
	);
	const [src, setSrc] = useState<string>(showCarousel ? slidesImage : '');

	const choose = (i: number) => {
		setSrc(slides[i].fullImage);
		setIndex(i);
	};

	const chooseFromZoom = (i: number) => {
		choose(i);
		carouselRef.current?.goToSlide(i);
	};

	useEffect(() => {
		setIndex(0);
		setSrc(showCarousel ? slidesImage : '');
	}, [showCarousel, slidesImage]);

	const { ribbons } = product ?? {};

	const filteredArray: Ribbon[] = ribbons
		? ribbons.filter((item) => !item.identifier.includes('DPS') && !item.identifier.includes('DPD'))
		: [];

	const uniqueArray: Ribbon[] = Array.from(
		filteredArray
			? filteredArray.reduce((map, obj) => map.set(obj.identifier, obj), new Map()).values()
			: []
	);

	const isMobile = useIsMobile();

	if (slides.length === 0 || !src) {
		return (
			<Skeleton variant="rectangular" width="100%" sx={{ height: { xs: '390px', md: '624px' } }} />
		);
	}

	return (
		<>
			{showCarousel ? (
				<ProductDetailsCarousel
					{...{
						slides,
						choose,
						ribbons: uniqueArray || [],
						index,
						src,
						alt: name,
						onClick: isMobile ? openImageDrawer.on : undefined,
						carouselRef,
					}}
				/>
			) : (
				<Grid container>
					<Grid item sx={productDetailsImageSX}>
						<ProductImage {...{ src, alt: name, ribbons: ribbons || [] }} />
					</Grid>
				</Grid>
			)}
			<ProductDetailsGalleryModal
				{...{
					slides,
					choose: chooseFromZoom,
					ribbons: [],
					index,
					src,
					alt: name,
					open: openImageDrawer.open,
					onClose: openImageDrawer.off,
				}}
			/>
		</>
	);
};
